import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";

import { Button } from "../button";
import { disableScroll, enableScroll } from "../../../helpers";
import { useFocusTrap } from "../../../helpers/hooks";
import closeIcon from "../../../media/icons/close-grey.svg";
import styles from "./modal.module.css";

const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]):not([type='hidden']), select, textarea, [tabindex]:not([tabindex='-1'])";

export const Modal = ({ children, className, classNameMain, classNameButton, closeModal, clearAllModals, isDisableCloseOutside }) => {
  const { t } = useTranslation();
  const contentRef = useRef(null);
  const previousFocusRef = useRef(null);
  const fallbackLabelId = useId();
  const [labelledBy, setLabelledBy] = useState(undefined);

  // Before the effect below, so the trap is gone when its cleanup refocuses the opener.
  useFocusTrap(contentRef);

  useEffect(() => {
    disableScroll();
    previousFocusRef.current = document.activeElement;

    const content = contentRef.current;
    const heading = content?.querySelector("h1, h2, h3");
    if (heading) {
      if (!heading.id) {
        heading.id = fallbackLabelId;
      }
      setLabelledBy(heading.id);
    }

    const focusable = content?.querySelectorAll(FOCUSABLE_SELECTOR);
    (focusable?.[0] || content)?.focus();

    return () => {
      enableScroll();
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
        previousFocusRef.current.focus();
      }
    };
  }, [fallbackLabelId]);

  const resetModals = () => {
    if (!isDisableCloseOutside) {
      clearAllModals();
    }
  };

  const handleClickOutside = () => {
    if (!isDisableCloseOutside) {
      resetModals();
    }
  };

  useEffect(() => {
    // Capture phase on purpose. react-select (and any other ARIA combobox/menu) calls
    // preventDefault() on every Escape it handles, open or not, so defaultPrevented can't tell us
    // whether a menu was open. aria-expanded can, but only before the menu handles the key: for a
    // real key press the browser flushes React's re-render between React's root listener and a
    // bubble-phase document listener, so by then aria-expanded has already flipped to false.
    const handleEscapeKeyDown = event => {
      if (event.key !== "Escape" || contentRef.current?.querySelector("[aria-expanded='true']")) {
        return;
      }
      if (!isDisableCloseOutside) {
        clearAllModals();
      }
    };

    document.addEventListener("keydown", handleEscapeKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleEscapeKeyDown, true);
    };
  }, [clearAllModals, isDisableCloseOutside]);

  return (
    <section className={cn(styles.wrapper, className)}>
      <div className={styles.leftAside} onClick={handleClickOutside} aria-hidden="true" />
      <main
        ref={contentRef}
        className={cn(styles.content, classNameMain)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : t("dashboard_common_dialog")}
        tabIndex="-1"
      >
        <Button className={cn("button--clear", styles.closeBtn, classNameButton)} onClick={closeModal}>
          <img src={closeIcon} alt="close" />
        </Button>
        {children}
      </main>
    </section>
  );
};
